import { useRef, useState, type DragEvent } from 'react';

const kb = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

// The proposal is always hand-coded: drop the .html or paste its source.
export const HtmlInput = ({ value, onChange }: { value: string; onChange: (html: string, fileName?: string) => void }) => {
  const [mode, setMode] = useState<'file' | 'paste'>('file');
  const [name, setName] = useState('');
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const read = (file: File | undefined) => {
    if (!file) return;
    file.text().then((t) => {
      setName(file.name);
      onChange(t, file.name);
    });
  };
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    read(e.dataTransfer.files[0]);
  };

  return (
    <div className="html-in">
      <div className="seg seg-sm" role="group" aria-label="Como enviar o HTML">
        <button type="button" aria-pressed={mode === 'file'} onClick={() => setMode('file')}>
          Arquivo .html
        </button>
        <button type="button" aria-pressed={mode === 'paste'} onClick={() => setMode('paste')}>
          Colar código
        </button>
      </div>
      {mode === 'file' ? (
        <div
          className="drop"
          data-over={over || undefined}
          data-filled={!!value || undefined}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={drop}
          onClick={() => input.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
        >
          <input ref={input} type="file" accept=".html,.htm,text/html" hidden onChange={(e) => read(e.target.files?.[0])} />
          {value ? (
            <>
              <strong>{name || 'HTML carregado'}</strong>
              <span>
                {kb(new Blob([value]).size)} · clique ou solte outro arquivo para trocar
              </span>
            </>
          ) : (
            <>
              <strong>Solte o .html da proposta aqui</strong>
              <span>ou clique para escolher o arquivo</span>
            </>
          )}
        </div>
      ) : (
        <textarea
          className="textarea code"
          spellCheck={false}
          placeholder="<!doctype html>…"
          value={value}
          onChange={(e) => {
            setName('');
            onChange(e.target.value);
          }}
        />
      )}
    </div>
  );
};

// Draft preview inside an opaque-origin frame. srcdoc resolves "#anchor" links against
// the admin page, so in-page links are scrolled by hand instead of navigated.
const ANCHORS = `<script>document.addEventListener('click',function(e){var a=e.target.closest&&e.target.closest('a[href^="#"]');if(!a)return;e.preventDefault();var t=document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1)));if(t)t.scrollIntoView({behavior:'smooth'});},true);</script>`;

// appended near </body>: anything before the doctype would throw the page into quirks mode
const withAnchors = (html: string): string => {
  const at = html.toLowerCase().lastIndexOf('</body>');
  return at === -1 ? html + ANCHORS : html.slice(0, at) + ANCHORS + html.slice(at);
};

export const DraftPreview = ({ html }: { html: string }) =>
  html ? (
    <iframe className="draft" title="Pré-visualização" sandbox="allow-scripts" srcDoc={withAnchors(html)} />
  ) : (
    <div className="draft draft-empty">
      <p>A pré-visualização aparece aqui.</p>
    </div>
  );
